import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  Description as ExcelIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import DashboardLayout from "@/components/DashboardLayout";
import {
  getBenchmarkingKunden,
  generateBerichtForKunde,
  downloadExcel,
  downloadPdf,
  downloadAllExcelZip,
  downloadAllPdfZip,
  getBerichtsjahr,
  setBerichtsjahr as setBerichtsjahrApi,
} from "@/api/berichtswesen";
import type { BerichtKundeDTO } from "@/api/types";

type ReportStatus = "idle" | "generating" | "ready" | "error";

interface KundeReportState {
  status: ReportStatus;
  errorMsg?: string;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const Reporting = () => {
  const [kunden, setKunden] = useState<BerichtKundeDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [berichtsjahr, setBerichtsjahr] = useState<number>(2024);

  // Per-customer report state keyed by IK (or "ALL" for aggregate)
  const [reportStates, setReportStates] = useState<Record<string, KundeReportState>>({});

  // Track whether "Alle Häuser" sequential run is in progress
  const [allRunning, setAllRunning] = useState(false);
  const abortRef = useRef(false);

  useEffect(() => {
    loadKunden();
    loadBerichtsjahr();
  }, []);

  const loadBerichtsjahr = async () => {
    try {
      const jahr = await getBerichtsjahr();
      setBerichtsjahr(jahr);
    } catch (err: any) {
      console.error("Fehler beim Laden des Berichtsjahrs", err);
    }
  };

  const handleBerichtsjahrChange = async (jahr: number) => {
    try {
      const saved = await setBerichtsjahrApi(jahr);
      setBerichtsjahr(saved);
    } catch (err: any) {
      setSnackbar(err?.message || "Fehler beim Speichern des Berichtsjahrs.");
    }
  };

  const loadKunden = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getBenchmarkingKunden();
      setKunden(data);
    } catch (err: any) {
      setError(err?.message || "Fehler beim Laden der Kunden.");
    } finally {
      setLoading(false);
    }
  };

  const getState = (key: string): KundeReportState =>
    reportStates[key] || { status: "idle" };

  const setState = (key: string, state: KundeReportState) =>
    setReportStates((prev) => ({ ...prev, [key]: state }));

  const isReady = (key: string) => getState(key).status === "ready";
  const isGenerating = (key: string) => getState(key).status === "generating";

  // ── Single house: one button generates Excel + PDF ────────────────
  const handleStartBericht = async (ik: string) => {
    setState(ik, { status: "generating" });
    try {
      await generateBerichtForKunde(ik);
      setState(ik, { status: "ready" });
    } catch (err: any) {
      setState(ik, { status: "error", errorMsg: err?.message });
      setSnackbar(err?.message || "Fehler beim Erstellen des Berichts.");
    }
  };

  const handleDownloadExcel = async (ik: string) => {
    try {
      const blob = await downloadExcel(ik);
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `Bericht_${ik}_${date}.xlsx`);
    } catch (err: any) {
      setSnackbar(err?.message || "Fehler beim Download.");
    }
  };

  const handleDownloadPdf = async (ik: string) => {
    try {
      const blob = await downloadPdf(ik);
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `Bericht_${ik}_${date}.pdf`);
    } catch (err: any) {
      setSnackbar(err?.message || "Fehler beim Download.");
    }
  };

  // ── Alle Häuser: runs all houses sequentially ─────────────────────
  const handleStartAll = async () => {
    setAllRunning(true);
    abortRef.current = false;

    // Mark all as generating
    const newStates: Record<string, KundeReportState> = {};
    for (const k of kunden) {
      newStates[k.ik] = { status: "generating" };
    }
    newStates["ALL"] = { status: "generating" };
    setReportStates((prev) => ({ ...prev, ...newStates }));

    // Run each house sequentially
    let allOk = true;
    for (const kunde of kunden) {
      if (abortRef.current) break;
      try {
        await generateBerichtForKunde(kunde.ik);
        setState(kunde.ik, { status: "ready" });
      } catch (err: any) {
        setState(kunde.ik, { status: "error", errorMsg: err?.message });
        allOk = false;
      }
    }

    setState("ALL", { status: allOk ? "ready" : "error" });
    setAllRunning(false);

    if (!allOk) {
      setSnackbar("Einige Berichte konnten nicht erstellt werden.");
    }
  };

  const handleDownloadAllExcel = async () => {
    try {
      const blob = await downloadAllExcelZip();
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `Berichte_Alle_${date}.zip`);
    } catch (err: any) {
      setSnackbar(err?.message || "Fehler beim Download.");
    }
  };

  const handleDownloadAllPdf = async () => {
    try {
      const blob = await downloadAllPdfZip();
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `Berichte_Alle_${date}.zip`);
    } catch (err: any) {
      setSnackbar(err?.message || "Fehler beim Download.");
    }
  };

  // ── Status rendering ──────────────────────────────────────────────
  const renderStatus = (key: string) => {
    const state = getState(key);
    switch (state.status) {
      case "generating":
        return <Chip label="wird erstellt…" size="small" color="warning" icon={<CircularProgress size={14} />} />;
      case "ready":
        return <Chip label="fertig" size="small" color="success" />;
      case "error":
        return <Chip label="Fehler" size="small" color="error" />;
      default:
        return <Chip label="—" size="small" variant="outlined" />;
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Berichtswesen
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="berichtsjahr-label">Berichtsjahr</InputLabel>
            <Select
              labelId="berichtsjahr-label"
              value={berichtsjahr}
              label="Berichtsjahr"
              onChange={(e) => handleBerichtsjahrChange(Number(e.target.value))}
            >
              <MenuItem value={2025}>2025</MenuItem>
              <MenuItem value={2024}>2024</MenuItem>
              <MenuItem value={2023}>2023</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>IK-Nummer</strong></TableCell>
                    <TableCell><strong>Bezeichnung</strong></TableCell>
                    <TableCell><strong>Ort</strong></TableCell>
                    <TableCell><strong>Benchmarkingbezeichnung</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Bericht</strong></TableCell>
                    <TableCell align="center"><strong>Download</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* ── Alle Häuser ─────────────────────────────── */}
                  <TableRow sx={{ backgroundColor: "action.hover" }}>
                    <TableCell><strong>Alle Häuser</strong></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell align="center">{renderStatus("ALL")}</TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={allRunning ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />}
                        onClick={handleStartAll}
                        disabled={allRunning}
                        sx={{ color: "white" }}
                      >
                        {allRunning ? "Berichte werden erstellt…" : "Alle Berichte starten"}
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Excel (ZIP) herunterladen">
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={handleDownloadAllExcel}
                            disabled={!isReady("ALL")}
                          >
                            <ExcelIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="PDF (ZIP) herunterladen">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={handleDownloadAllPdf}
                            disabled={!isReady("ALL")}
                          >
                            <PdfIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* ── Einzelne Häuser ─────────────────────────── */}
                  {kunden.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Typography color="text.secondary">
                          Keine Kunden mit Benchmarking vorhanden
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    kunden.map((kunde) => (
                      <TableRow key={kunde.id} hover>
                        <TableCell>{kunde.ik}</TableCell>
                        <TableCell>{kunde.name}</TableCell>
                        <TableCell>{kunde.ort ?? "–"}</TableCell>
                        <TableCell>{kunde.benchmarkingBezeichnung ?? "–"}</TableCell>
                        <TableCell align="center">{renderStatus(kunde.ik)}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={isGenerating(kunde.ik) ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />}
                            onClick={() => handleStartBericht(kunde.ik)}
                            disabled={isGenerating(kunde.ik) || allRunning}
                            sx={{ color: "white" }}
                          >
                            Bericht starten
                          </Button>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Excel herunterladen">
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleDownloadExcel(kunde.ik)}
                                disabled={!isReady(kunde.ik)}
                              >
                                <ExcelIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="PDF herunterladen">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDownloadPdf(kunde.ik)}
                                disabled={!isReady(kunde.ik)}
                              >
                                <PdfIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSnackbar(null)}>
          {snackbar}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default Reporting;
